import Alpine from 'alpinejs';

import { PACK_CONTENT_DEFAULTS, PACKS, volumeDiscountPercent } from './packs';
import type {
  ButtonState,
  PackContent,
  PackFeatures,
  PackFeatureSubjectLine,
  PackPrice,
  Paper,
  ResolvedPack,
  SectionSettings,
  YearLevel,
} from './types';

const SECOND_TOAST_DISMISS_MS = 8000;
const TIER_TOAST_DISMISS_MS = 5000;

const DEFAULT_SETTINGS: SectionSettings = {
  yearLevels: [],
  subjectIcons: {},
  tooltips: {
    online: '',
    interactivePdf: '',
    pdf: '',
    autoMarking: '',
    answerSheets: '',
  },
  toast: {
    headingTemplate: '{type} added to cart',
    buyAnotherLabel: 'Buy for another child',
    proceedLabel: 'Proceed to cart',
  },
  tierToasts: { '3': '', '5': '', '8': '' },
  currency: {
    isoCode: 'AUD',
    moneyFormat: '${{amount}}',
    moneyWithCurrencyFormat: '${{amount}} AUD',
  },
};

interface PaperPayload {
  papers: Paper[];
  _truncated?: boolean;
}

interface PackContentMap {
  [contentKey: string]: PackContent | null;
}

function safeParseJson<T>(text: string | null, fallback: T): T {
  if (!text) return fallback;
  try {
    return JSON.parse(text) as T;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('past-papers-builder: failed to parse JSON payload', e);
    return fallback;
  }
}

/**
 * Delimiter rules for every placeholder Shopify can put in a money format,
 * mirroring the switch in Shopify's own `currency.js`. Markets hand us the
 * presentment currency's format, so a store selling in AUD still has to render
 * `Rp {{amount_with_comma_separator}}` for the Indonesia market.
 */
const MONEY_PLACEHOLDERS: Record<string, { decimals: number; thousands: string; decimal: string }> = {
  amount: { decimals: 2, thousands: ',', decimal: '.' },
  amount_no_decimals: { decimals: 0, thousands: ',', decimal: '.' },
  amount_with_comma_separator: { decimals: 2, thousands: '.', decimal: ',' },
  amount_no_decimals_with_comma_separator: { decimals: 0, thousands: '.', decimal: ',' },
  amount_with_apostrophe_separator: { decimals: 2, thousands: "'", decimal: '.' },
  amount_with_space_separator: { decimals: 2, thousands: ' ', decimal: ',' },
  amount_no_decimals_with_space_separator: { decimals: 0, thousands: ' ', decimal: '.' },
  amount_with_period_and_space_separator: { decimals: 2, thousands: ' ', decimal: '.' },
};

/**
 * Substitute a cents amount into a Shopify money-format template. Individual
 * paper prices come pre-formatted from Liquid (`| money`) so this only runs
 * for client-computed pack totals.
 *
 * Unrecognised placeholders fall back to `amount` rather than being left in
 * place, so a format we don't know about degrades to a plausible price instead
 * of leaking `{{...}}` into the pack card.
 */
function substituteAmount(format: string, cents: number): string {
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name: string) => {
    const rule = MONEY_PLACEHOLDERS[name] ?? MONEY_PLACEHOLDERS.amount;
    const [whole, fraction] = (cents / 100).toFixed(rule.decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, rule.thousands);
    return fraction ? grouped + rule.decimal + fraction : grouped;
  });
}

Alpine.data('pastPapersBuilder', () => ({
  // ─── State ────────────────────────────────────────────────────────────
  papers: [] as Paper[],
  /** Difficulty code of the selected year pill; null = no filter (all years). */
  selectedYear: null as string | null,
  selectedSubjects: [] as string[],
  buttonStates: {} as Record<string, ButtonState>,
  buttonErrors: {} as Record<string, string>,
  /** Past-paper variants currently in the cart (refreshed on init + after every add). */
  _cartPastPaperCount: 0,
  /** Highest tier-toast threshold already shown this session; suppresses re-fires. */
  _lastFiredTier: 0,
  /** Year-level codes whose accordions are currently expanded. */
  openAccordions: [] as string[],
  packContent: {} as PackContentMap,
  settings: DEFAULT_SETTINGS as SectionSettings,
  tierToast: { visible: false, message: '' },
  secondToast: { visible: false, heading: '' },
  _tierToastTimer: 0,
  _secondToastTimer: 0,
  _cartController: null as AbortController | null,

  // ─── Init ─────────────────────────────────────────────────────────────
  init() {
    const root = this.$el as HTMLElement;

    const papersScript = root.querySelector<HTMLScriptElement>('script[data-papers]');
    const payload = safeParseJson<PaperPayload>(papersScript?.textContent || null, { papers: [] });
    this.papers = payload.papers;

    if (payload._truncated) {
      // eslint-disable-next-line no-console
      console.warn(
        'past-papers-builder: collection has more than 50 products; some papers were omitted from the payload.',
      );
    }

    const contentScript = root.querySelector<HTMLScriptElement>('script[data-pack-content]');
    this.packContent = safeParseJson<PackContentMap>(contentScript?.textContent || null, {});

    const settingsScript = root.querySelector<HTMLScriptElement>('script[data-section-settings]');
    const parsed = safeParseJson<Partial<SectionSettings>>(settingsScript?.textContent || null, {});
    this.settings = {
      ...DEFAULT_SETTINGS,
      ...parsed,
      tooltips: { ...DEFAULT_SETTINGS.tooltips, ...(parsed.tooltips || {}) },
      toast: { ...DEFAULT_SETTINGS.toast, ...(parsed.toast || {}) },
      tierToasts: { ...DEFAULT_SETTINGS.tierToasts, ...(parsed.tierToasts || {}) },
      currency: { ...DEFAULT_SETTINGS.currency, ...(parsed.currency || {}) },
    };

    // Seed cart state so tier-toast thresholds reflect papers already in the
    // cart (e.g. from a previous visit). After this initial read, any tier
    // already crossed is treated as already fired so we don't show stale
    // nudges on the first add.
    void this._refreshCart().then(() => {
      this._lastFiredTier = this._tierForCount(this._cartPastPaperCount);
    });
  },

  // ─── Derived: subjects, years ─────────────────────────────────────────
  availableSubjects(): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    (this.papers as Paper[]).forEach((p) => {
      if (p.subject && !seen.has(p.subject)) {
        seen.add(p.subject);
        result.push(p.subject);
      }
    });
    return result.slice().sort((a, b) => this._subjectSortCompare(a, b));
  },

  /**
   * Subjects used for filtering, pack resolution and the accordion rows. An
   * empty selection means "no filter": landing, select-all and deselect-all
   * all show the full catalogue. Checking a subset filters down to it.
   */
  effectiveSubjects(): string[] {
    const selected = this.selectedSubjects as string[];
    return selected.length > 0 ? selected : this.availableSubjects();
  },

  /**
   * Effective subjects in display order (popularity, not selection order).
   * Used for the pack "Includes" lines and the build-your-own accordions so
   * the order stays consistent with the Step 2 grid.
   */
  displaySubjects(): string[] {
    return this.effectiveSubjects()
      .slice()
      .sort((a, b) => this._subjectSortCompare(a, b));
  },

  /**
   * Popularity rank for a subject — Mathematics > English > Science > Digital
   * Technologies, then anything else falls through to alphabetical. Handles
   * the common "Maths" / "Mathematics" alias.
   */
  _subjectRank(subject: string): number {
    const handle = subject.toLowerCase().trim();
    if (handle === 'mathematics' || handle === 'maths') return 0;
    if (handle === 'english') return 1;
    if (handle === 'science') return 2;
    if (handle === 'digital technologies' || handle === 'digital tech') return 3;
    return 99;
  },

  _subjectSortCompare(a: string, b: string): number {
    const rankDiff = this._subjectRank(a) - this._subjectRank(b);
    if (rankDiff !== 0) return rankDiff;
    return a.localeCompare(b);
  },

  /**
   * Year level pills. Source: section settings `yearLevels`, populated
   * server-side from the union of `custom.difficulty_levels` values across
   * available variants. Already deduped, sorted (INT first, then
   * alphabetical), and label-resolved via the locale `t` filter for
   * per-market translation. None is selected on load — like the subject
   * cards, no selection means "no filter".
   */
  availableYearLevels(): YearLevel[] {
    return (this.settings as SectionSettings).yearLevels;
  },

  /** Resolve a difficulty code to its localised label, falling back to the code. */
  yearLevelLabel(code: string): string {
    const yl = (this.settings as SectionSettings).yearLevels.find((y: YearLevel) => y.code === code);
    return yl ? yl.label : code;
  },

  /**
   * For papers that cover multiple year levels (e.g. an ICAS DT 3/4 paper),
   * produce a joined label like "Year 3 / Year 4". Returns '' when the paper
   * applies to a single year level so the markup can skip the row.
   */
  paperYearLevelRange(paper: Paper): string {
    if (!paper.difficulties || paper.difficulties.length <= 1) return '';
    const order = (this.settings as SectionSettings).yearLevels.map((y: YearLevel) => y.code);
    const rank = (code: string) => {
      const i = order.indexOf(code);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return paper.difficulties
      .slice()
      .sort((a: string, b: string) => rank(a) - rank(b))
      .map((code: string) => this.yearLevelLabel(code))
      .join(' / ');
  },

  /**
   * Resolve a subject name to its icon URL. Handleizes the subject and then
   * tries a few common aliases (e.g. "Maths" / "Mathematics") so the catalogue
   * doesn't have to use a single canonical name. Returns '' if no icon exists,
   * which the markup treats as a signal to hide the icon entirely.
   */
  subjectIconUrl(subject: string): string {
    const icons = (this.settings as SectionSettings).subjectIcons;
    const handle = subject
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return icons[handle] || '';
  },

  // ─── Year selection ───────────────────────────────────────────────────
  selectYear(yearLevel: string) {
    // Subject selections, cart-driven button states and button errors are
    // deliberately preserved across year switches (spec decision — deviates
    // from the prototype, which reset subjects to all-selected).
    this.selectedYear = yearLevel;
    // The selected year's accordion opens automatically.
    this.openAccordions = [yearLevel];
  },

  // ─── Subject selection ────────────────────────────────────────────────
  toggleSubject(subject: string) {
    const subjects = this.selectedSubjects as string[];
    if (subjects.includes(subject)) {
      this.selectedSubjects = subjects.filter((s) => s !== subject);
    } else {
      this.selectedSubjects = [...subjects, subject];
    }
  },

  toggleAccordion(code: string) {
    const open = this.openAccordions as string[];
    if (open.includes(code)) {
      this.openAccordions = open.filter((c) => c !== code);
    } else {
      this.openAccordions = [...open, code];
    }
  },

  // ─── Papers for accordion ─────────────────────────────────────────────
  papersFor(subject: string, code: string): Paper[] {
    return (this.papers as Paper[])
      .filter((p) => p.subject === subject && p.difficulties.includes(code))
      .slice()
      .sort((a, b) => parseInt(b.paperYear, 10) - parseInt(a.paperYear, 10));
  },

  /**
   * Year levels to render as accordions: every year level when All is
   * selected, otherwise just the selected one. Levels where no effective
   * subject has papers are skipped.
   */
  visibleYearLevels(): YearLevel[] {
    const levels = (this.settings as SectionSettings).yearLevels;
    const candidates =
      this.selectedYear === null ? levels : levels.filter((yl: YearLevel) => yl.code === this.selectedYear);
    return candidates.filter((yl: YearLevel) =>
      this.effectiveSubjects().some((s: string) => this.papersFor(s, yl.code).length > 0),
    );
  },

  /** True if at least one effective subject has papers at a visible year level. */
  hasAnyIndividualPapers(): boolean {
    return this.visibleYearLevels().length > 0;
  },

  /**
   * Distinct individual papers across effective subjects at the visible year
   * levels. Counted by variant ID so a multi-level paper (e.g. DT 3/4) that
   * appears under several year accordions is only counted once.
   */
  totalAvailablePapers(): number {
    const variantIds = new Set<number>();
    this.visibleYearLevels().forEach((yl: YearLevel) => {
      this.effectiveSubjects().forEach((s: string) => {
        this.papersFor(s, yl.code).forEach((p: Paper) => variantIds.add(p.variantId));
      });
    });
    return variantIds.size;
  },

  /** ` – Year 5`-style heading suffix; empty while no year is selected. */
  headingSuffix(): string {
    if (this.selectedYear === null) return '';
    return ` – ${this.yearLevelLabel(this.selectedYear)}`;
  },

  // ─── Pack resolution ──────────────────────────────────────────────────
  resolvePack(packId: string): Paper[] {
    // Packs need an explicit year AND explicit subjects: a pack assembled
    // from unfiltered subjects would bundle products the parent never chose
    // (client call — avoids refund requests, esp. Digital Technologies).
    const subjects = this.selectedSubjects as string[];
    if (this.selectedYear === null || subjects.length === 0) return [];
    const pack = PACKS.find((p) => p.id === packId);
    if (!pack) return [];
    const code = this.selectedYear;
    const allPapers = this.papers as Paper[];
    const papersAtYear = allPapers.filter((p) => p.difficulties.includes(code));
    const yearLevelOrder = (this.settings as SectionSettings).yearLevels.map((y: YearLevel) => y.code);
    return pack.rule({
      papers: papersAtYear,
      allPapers,
      subjects,
      selectedYear: code,
      yearLevelOrder,
    });
  },

  packPrice(packId: string): PackPrice {
    const resolved = this.resolvePack(packId);
    const sum = resolved.reduce((acc: number, p: Paper) => acc + p.price, 0);
    const discountPercent = volumeDiscountPercent(resolved.length);
    const discounted = Math.round(sum * (1 - discountPercent / 100));
    return { sum, discounted, discountPercent };
  },

  /**
   * Pack cards to render. Walks PACKS in tier order (Foundation, Essential,
   * Complete) and:
   *   - Drops packs that resolve to zero variants.
   *   - Hides any higher-tier pack whose variant set equals an already-shown
   *     lower-tier pack — with the new rules a sparse catalogue can produce
   *     identical Foundation/Essential/Complete sets, and we don't want two
   *     cards offering the same bundle.
   */
  visiblePacks(): ResolvedPack[] {
    const result: ResolvedPack[] = [];
    const shownVariantSets: Set<number>[] = [];

    for (const pack of PACKS) {
      const resolved = this.resolvePack(pack.id) as Paper[];
      if (resolved.length === 0) continue;
      const variantSet = new Set<number>(resolved.map((p) => p.variantId));
      const isDuplicate = shownVariantSets.some(
        (shown) => shown.size === variantSet.size && Array.from(variantSet).every((v) => shown.has(v)),
      );
      if (isDuplicate) continue;
      shownVariantSets.push(variantSet);
      const content = this.packContent[pack.contentKey] || PACK_CONTENT_DEFAULTS[pack.contentKey];
      result.push({
        id: pack.id,
        contentKey: pack.contentKey,
        displayName: content?.displayName || pack.contentKey,
        description: content?.description || '',
        tagline: content?.tagline || '',
        badgeText: content?.badgeText || '',
        popular: content?.popular || false,
      } as ResolvedPack);
    }
    return result;
  },

  // ─── Pack feature lines ───────────────────────────────────────────────
  packFeatures(packId: string): PackFeatures {
    const resolved = this.resolvePack(packId);

    // Per-subject paper counts in display order (popularity).
    const subjectLines: PackFeatureSubjectLine[] = [];
    this.displaySubjects().forEach((subject: string) => {
      const subjectPapers = resolved.filter((p) => p.subject === subject);
      if (subjectPapers.length === 0) return;
      const paperWord = subjectPapers.length === 1 ? 'paper' : 'papers';
      const label = `${subjectPapers.length}x ${subject} ${paperWord}`;
      // Tooltip uses the real product title — for joined-year products (e.g.
      // "ICAS DT 3/4 2018"), the product title carries the correct year range
      // rather than a derived single year.
      const tooltip = subjectPapers
        .slice()
        .sort((a: Paper, b: Paper) => parseInt(b.paperYear, 10) - parseInt(a.paperYear, 10))
        .map((p: Paper) => {
          const range = this.paperYearLevelRange(p);
          const rangePart = range ? ` (${this._escape(range)})` : '';
          return `${this._escape(p.productTitle)}${rangePart} – ${this._escape(p.mode)}`;
        })
        .join('<br>');
      subjectLines.push({ subject, label, tooltip });
    });

    // Modes present (preserving Online → Interactive PDF → PDF order)
    const modeOrder = ['Online', 'Interactive PDF', 'PDF'];
    const modesPresent = modeOrder.filter((m) => resolved.some((p) => p.mode === m));
    const formatLabel = this._joinWithAmpersand(modesPresent);
    const formatTooltip = modesPresent
      .map((m) => this._tooltipForMode(m))
      .filter(Boolean)
      .join('<br>');

    // Capability line. When both capabilities are present they read as a
    // mid-sentence pair: "Auto-marking & answer sheets" (lowercase second
    // item). When only one capability is present it renders capitalised on
    // its own.
    const capabilities: string[] = [];
    if (modesPresent.includes('Online') || modesPresent.includes('Interactive PDF')) {
      capabilities.push('Auto-marking');
    }
    // Online papers ship with answer sheets as well as auto-marking, so a
    // pack of recent (Online-mode) papers reads "Auto-marking & answer sheets".
    if (modesPresent.includes('PDF') || modesPresent.includes('Online')) {
      capabilities.push('Answer sheets');
    }
    const capabilityLabel =
      capabilities.length <= 1
        ? capabilities.join(' & ')
        : `${capabilities[0]} & ${capabilities
            .slice(1)
            .map((c) => c.charAt(0).toLowerCase() + c.slice(1))
            .join(' & ')}`;

    return {
      subjectLines,
      formatLabel,
      formatTooltip,
      capabilityLabel,
    };
  },

  _joinWithAmpersand(items: string[]): string {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} & ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
  },

  _tooltipForMode(mode: string): string {
    if (mode === 'Online') return this.settings.tooltips.online;
    if (mode === 'Interactive PDF') return this.settings.tooltips.interactivePdf;
    if (mode === 'PDF') return this.settings.tooltips.pdf;
    return '';
  },

  modeTooltip(mode: string): string {
    return this._tooltipForMode(mode);
  },

  _escape(text: string): string {
    return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  },

  // ─── Money formatting ─────────────────────────────────────────────────
  /**
   * Format a cents amount using the section's money-format template. Used for
   * client-computed pack totals; per-paper prices come pre-formatted from
   * Liquid as `priceFormatted` on each Paper entry.
   */
  formatMoney(cents: number): string {
    const settings = this.settings.currency;
    const format = settings.moneyFormat || '${{amount}}';
    return substituteAmount(format, cents);
  },

  // ─── Add to cart ──────────────────────────────────────────────────────
  async addIndividual(variantId: number) {
    const buttonId = `paper:${variantId}`;
    if (this.buttonStates[buttonId] === 'loading') return;
    this._setButtonState(buttonId, 'loading');
    this._clearButtonError(buttonId);
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      this._setButtonState(buttonId, 'added');
      this._showSecondToast('past paper');
      // Refresh cart first so the tier check uses the true cart count
      // (existing items + this add), then evaluate whether to nudge.
      void this._refreshCart().then(() => this._maybeFireTierToast());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('past-papers-builder: individual add failed', e);
      this._setButtonState(buttonId, 'idle');
      this._setButtonError(buttonId, 'Could not add to cart. Please try again.');
    }
  },

  /**
   * Remove an individual paper from the cart. Triggered by clicking the
   * "Added" button (which swaps to "Remove" on hover). Uses `/cart/update.js`
   * because `/cart/change.js`'s `id` field requires a line index or line key,
   * not a variant ID — `update.js` takes a variant-ID-keyed `updates` map.
   */
  async removeIndividual(variantId: number) {
    const buttonId = `paper:${variantId}`;
    if (this.buttonStates[buttonId] === 'loading') return;
    this._setButtonState(buttonId, 'loading');
    this._clearButtonError(buttonId);
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: 0 } }),
      });
      if (!response.ok) throw new Error(`Cart remove failed: ${response.status}`);
      this._setButtonState(buttonId, 'idle');
      void this._refreshCart();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('past-papers-builder: individual remove failed', e);
      this._setButtonState(buttonId, 'added');
      this._setButtonError(buttonId, 'Could not remove from cart. Please try again.');
    }
  },

  async addPack(packId: string) {
    const buttonId = `pack:${packId}`;
    if (this.buttonStates[buttonId] === 'loading') return;
    const resolved = this.resolvePack(packId);
    if (resolved.length === 0) return;
    this._setButtonState(buttonId, 'loading');
    this._clearButtonError(buttonId);
    try {
      const items = resolved.map((p) => ({ id: p.variantId, quantity: 1 }));
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      this._setButtonState(buttonId, 'added');
      this._showSecondToast('pack');
      // Pack adds change the cart count but per spec only individual adds
      // trigger tier toasts — refresh cart for the badge / future tier checks,
      // no _maybeFireTierToast call here.
      void this._refreshCart();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('past-papers-builder: pack add failed', e);
      this._setButtonState(buttonId, 'idle');
      this._setButtonError(buttonId, 'Could not add to cart. Please try again.');
    }
  },

  /**
   * Remove a pack's variants from the cart. Triggered by clicking the "Added
   * to cart" button (which swaps to "Remove from cart" on hover). Sets every
   * pack-resolved variant's quantity to 0 in a single `/cart/update.js` call.
   */
  async removePack(packId: string) {
    const buttonId = `pack:${packId}`;
    if (this.buttonStates[buttonId] === 'loading') return;
    const resolved = this.resolvePack(packId);
    if (resolved.length === 0) return;
    this._setButtonState(buttonId, 'loading');
    this._clearButtonError(buttonId);
    try {
      const updates: Record<string, number> = {};
      resolved.forEach((p: Paper) => {
        updates[p.variantId] = 0;
      });
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error(`Cart remove failed: ${response.status}`);
      this._setButtonState(buttonId, 'idle');
      void this._refreshCart();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('past-papers-builder: pack remove failed', e);
      this._setButtonState(buttonId, 'added');
      this._setButtonError(buttonId, 'Could not remove from cart. Please try again.');
    }
  },

  _setButtonState(buttonId: string, state: ButtonState) {
    this.buttonStates = { ...this.buttonStates, [buttonId]: state };
  },

  _setButtonError(buttonId: string, message: string) {
    this.buttonErrors = { ...this.buttonErrors, [buttonId]: message };
  },

  _clearButtonError(buttonId: string) {
    if (!(buttonId in this.buttonErrors)) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [buttonId]: _omitted, ...rest } = this.buttonErrors;
    this.buttonErrors = rest;
  },

  /**
   * Fetch /cart.js, update the header badge, and count how many past-paper
   * variants are in the cart so tier-toast thresholds reflect existing items
   * (not just session adds). Parallel adds resolve out of order, so each
   * call aborts the previous in-flight request.
   */
  async _refreshCart() {
    if (this._cartController) {
      this._cartController.abort();
    }
    const controller = new AbortController();
    this._cartController = controller;
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) return;
      const cart = (await response.json()) as {
        item_count?: number;
        items?: { variant_id: number; quantity: number }[];
      };
      const itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;
      this._writeCartBadge('.header__cart-link', itemCount);

      const paperIds = new Set((this.papers as Paper[]).map((p) => p.variantId));
      let pastPaperCount = 0;
      (cart.items || []).forEach((item) => {
        if (paperIds.has(item.variant_id)) pastPaperCount += item.quantity;
      });
      this._cartPastPaperCount = pastPaperCount;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.warn('past-papers-builder: cart refresh failed', e);
    } finally {
      if (this._cartController === controller) {
        this._cartController = null;
      }
    }
  },

  _writeCartBadge(selector: string, count: number) {
    document.querySelectorAll<HTMLElement>(selector).forEach((node) => {
      const label = node.querySelector<HTMLElement>('label');
      if (label) {
        if (count > 0) {
          label.textContent = String(count);
        } else {
          label.remove();
        }
        return;
      }
      if (count > 0) {
        // No existing label — create one to match the header.liquid markup.
        const newLabel = document.createElement('label');
        newLabel.setAttribute('aria-label', 'Cart Items');
        newLabel.textContent = String(count);
        node.appendChild(newLabel);
      }
    });
  },

  // ─── Toasts ───────────────────────────────────────────────────────────
  _showSecondToast(type: 'past paper' | 'pack') {
    // Capitalise the substituted word only when `{type}` is the first thing
    // in the template (so the default "{type} added to cart" reads "Pack added
    // to cart"). A merchant-authored template like "You added a {type}" keeps
    // the token lowercase, which reads naturally.
    const template = this.settings.toast.headingTemplate || '{type} added to cart';
    const tokenAtStart = template.indexOf('{type}') === 0;
    const value = tokenAtStart ? type.charAt(0).toUpperCase() + type.slice(1) : type;
    this.secondToast = { visible: true, heading: template.replace('{type}', value) };
    if (this._secondToastTimer) window.clearTimeout(this._secondToastTimer);
    this._secondToastTimer = window.setTimeout(() => {
      this.dismissSecondToast();
    }, SECOND_TOAST_DISMISS_MS);
  },

  dismissSecondToast() {
    this.secondToast = { ...this.secondToast, visible: false };
    if (this._secondToastTimer) {
      window.clearTimeout(this._secondToastTimer);
      this._secondToastTimer = 0;
    }
  },

  /** Highest tier-toast threshold the given paper count has reached, or 0. */
  _tierForCount(count: number): number {
    if (count >= 8) return 8;
    if (count >= 5) return 5;
    if (count >= 3) return 3;
    return 0;
  },

  _maybeFireTierToast() {
    const tier = this._tierForCount(this._cartPastPaperCount);
    if (tier === 0 || tier <= this._lastFiredTier) return;
    const message = this.settings.tierToasts[String(tier)];
    if (!message) return;
    this._lastFiredTier = tier;
    this.tierToast = { visible: true, message };
    if (this._tierToastTimer) window.clearTimeout(this._tierToastTimer);
    this._tierToastTimer = window.setTimeout(() => {
      this.dismissTierToast();
    }, TIER_TOAST_DISMISS_MS);
  },

  dismissTierToast() {
    this.tierToast = { ...this.tierToast, visible: false };
    if (this._tierToastTimer) {
      window.clearTimeout(this._tierToastTimer);
      this._tierToastTimer = 0;
    }
  },

  proceedToCart() {
    window.location.href = '/cart';
  },

  // ─── Reset ────────────────────────────────────────────────────────────
  resetSelections() {
    // Back to the page-load default: no year, no subject filter,
    // all accordions closed.
    this.selectedYear = null;
    this.selectedSubjects = [];
    this.buttonStates = {};
    this.buttonErrors = {};
    this.openAccordions = [];
    this.dismissSecondToast();
    this.dismissTierToast();
    // _cartPastPaperCount and _lastFiredTier intentionally NOT reset — the
    // cart still contains the previous child's items, and we don't want to
    // re-fire tier toasts the customer has already seen.

    // Scroll the whole page to the top so the site header + Step 1 instructions
    // are visible (scrolling to the section root left "1. Select year level"
    // cropped under the sticky header).
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  },
}));
